import { forwardRef, type ReactNode } from 'react';
import { BedDouble, CalendarClock, ShieldAlert, ShieldCheck, ShieldQuestion, Stethoscope, Wallet } from 'lucide-react';
import type { AdmissionCareRole, AdmissionCareTeamDto } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { cn, dateOnly } from '@/lib/utils';
import { Badge, StatusBadge, statusLabel } from '@/components/ui/primitives';
import { StickyPatientBar } from '@/components/clinical-shell/ClinicalShell';
import { PatientAvatar } from '@/components/PatientAvatar';
import { ageText } from '../../opd/emr/EmrPatientBar';
import type { Admission } from './types';

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function careRoleLabel(role: AdmissionCareRole): string {
  return tr('ipd', `ws_role_${role}`);
}

function careRoleShort(role: AdmissionCareRole): string {
  return tr('ipd', `ws_role_short_${role}`);
}

function AllergyStrip({ patient }: { patient: Admission['patient'] }) {
  if (patient.allergies.length > 0) {
    return (
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-destructive">
          <ShieldAlert className="h-3.5 w-3.5" aria-hidden /> {tr('ipd', 'allergies')}
        </span>
        {patient.allergies.map((allergy) => (
          <span
            key={allergy.id}
            className="rounded-full border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive"
            title={allergy.reaction ?? undefined}
          >
            {allergy.substance}
            {allergy.severity === 'severe' && ' !'}
          </span>
        ))}
      </div>
    );
  }
  if (patient.allergyStatus === 'nka') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-success">
        <ShieldCheck className="h-3.5 w-3.5" aria-hidden /> {tr('ipd', 'ws_no_known_allergies')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-warning">
      <ShieldQuestion className="h-3.5 w-3.5" aria-hidden /> {tr('ipd', 'ws_allergies_not_recorded')}
    </span>
  );
}

function CareTeamChips({ team, fallback, onOpen }: { team: AdmissionCareTeamDto | undefined; fallback: Admission['doctor']; onOpen?: () => void }) {
  const members = team?.members ?? [{ id: fallback.id, userId: fallback.id, role: 'primary_consultant' as const, name: fallback.name }];
  const chips = (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
      <Stethoscope className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
      <span className="sr-only">{tr('ipd', 'ws_care_team')}</span>
      {members.map((member) => (
        <span
          key={member.id}
          className={cn(
            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
            member.role === 'primary_consultant' ? 'border-primary/30 bg-primary/10 font-medium text-primary' : 'bg-muted/40 text-foreground/80',
          )}
        >
          {member.name ?? tr('ipd', 'ws_unknown_doctor')}
          <span className="text-[10px] uppercase tracking-wide opacity-70">{careRoleShort(member.role)}</span>
        </span>
      ))}
    </span>
  );
  if (!onOpen) return chips;
  return (
    <button type="button" onClick={onOpen} className="min-w-0 rounded-lg text-start focus-ring" aria-label={tr('ipd', 'ws_open_care_team')}>
      {chips}
    </button>
  );
}

export const AdmissionPatientBar = forwardRef<
  HTMLDivElement,
  { admission: Admission; team: AdmissionCareTeamDto | undefined; actions?: ReactNode; onOpenCareTeam?: () => void }
>(function AdmissionPatientBar({ admission, team, actions, onOpenCareTeam }, ref) {
  const { patient } = admission;
  const age = ageText(patient.dob, patient.approximateAgeYears ?? null);
  const facts = [age, patient.sex ? statusLabel(patient.sex) : null].filter(Boolean);
  const place = admission.bed ? `${admission.bed.room.ward.name} · ${admission.bed.code}` : tr('ipd', 'ws_no_bed');

  return (
    <StickyPatientBar ref={ref}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 bg-gradient-to-r from-primary/[0.07] via-transparent to-transparent px-4 py-3 rtl:bg-gradient-to-l">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <PatientAvatar
            name={patient.fullName}
            photoKey={patient.photoUrl}
            className="hidden h-12 w-12 shrink-0 place-items-center rounded-2xl bg-primary text-base font-semibold text-primary-foreground shadow-md shadow-primary/25 sm:grid"
            fallback={initialsOf(patient.fullName)}
          />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-lg font-semibold leading-tight">{patient.fullName}</h1>
              <span className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground" dir="ltr">
                {patient.mrn}
              </span>
              <StatusBadge status={admission.status} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {facts.length > 0 && <span className="font-medium text-foreground/80">{facts.join(' · ')}</span>}
              <span className="inline-flex items-center gap-1">
                <BedDouble className="h-3 w-3" aria-hidden /> {place}
              </span>
              <span className="inline-flex items-center gap-1" title={tr('ipd', 'ws_admitted_on_x', { date: dateOnly(admission.admittedAt) })}>
                <CalendarClock className="h-3 w-3" aria-hidden /> {tr('ipd', 'ws_day_x', { n: admission.lengthOfStayDays })}
              </span>
              <span className="inline-flex items-center gap-1">
                <Wallet className="h-3 w-3" aria-hidden />
                {admission.payer ? <Badge variant="info">{admission.payer.name}</Badge> : tr('ipd', 'ws_self_pay')}
              </span>
              <span className="font-mono text-[11px]" dir="ltr">
                {admission.number}
              </span>
            </div>
          </div>
        </div>
        {actions && <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">{actions}</div>}
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t px-4 py-2">
        <AllergyStrip patient={patient} />
        <CareTeamChips team={team} fallback={admission.doctor} onOpen={onOpenCareTeam} />
      </div>
    </StickyPatientBar>
  );
});
